import { useEffect, useState } from "react";
import {
    TextField,
    Button,
    Grid,
    Typography,
    IconButton,
    Paper,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import InvoicePreview from "../components/InvoicePreview";
import axios from "axios";

const InvoiceForm = () => {
    const [items, setItems] = useState([
        { description: "", hsn: "", unit: "", quantity: "", price: "" },
    ]);
    const [clientName, setClientName] = useState("");
    const [clientAddress, setClientAddress] = useState("");
    const [clientMobile, setClientMobile] = useState("");
    const [gstin, setGstin] = useState("");
    const [invoiceNo, setInvoiceNumber] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null); // Store data snapshot
    const addItem = () => {
        setItems([...items, { description: "", hsn: "", unit: "", quantity: "", price: "" }]);
    };
    const [error, setError] = useState(false);

    const validateGstin = (value) => {
        const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        return gstinRegex.test(value.toUpperCase());
    };

    const handleChange = (e) => {
        const value = e.target.value.toUpperCase();
        setGstin(value);
        setError(!validateGstin(value));
    };

    const removeItem = (i) => {
        const updated = [...items];
        updated.splice(i, 1);
        setItems(updated);
    };

    const updateItem = (i, key, value) => {
        const updated = [...items];
        updated[i][key] = value;
        setItems(updated);
    };

    // Calculate amounts
    const calculatedItems = items.map(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        const total = quantity * price;
        return {
            product: item.description,
            hsn: item.hsn,
            unitPrice: price.toFixed(2),
            quantity: quantity,
            total: total.toFixed(2),
        };
    });

    const subtotal = calculatedItems.reduce((sum, item) => sum + parseFloat(item.total), 0);
    const cgst = +(subtotal * 0.09).toFixed(2);
    const sgst = +(subtotal * 0.09).toFixed(2);
    const grossAmount = +(subtotal + cgst + sgst).toFixed(2);

    const invoiceData = {
        customerName: clientName,
        address: clientAddress,
        customerNumber: clientMobile,
        gstin: gstin,
        invoiceNo,
        invoiceDate,
        dueDate,
        items: calculatedItems,
        cgst,
        sgst,
        grossAmount
    };
    const handlePreview = () => {
        setPreviewData({ ...invoiceData });
        setShowPreview(true);
    };

    const resetForm = () => {
        setClientName('');
        setClientAddress('');
        setClientMobile('')
        setGstin('');
        setInvoiceNumber('');
        setInvoiceDate('');
        setDueDate('');
        setShowPreview(false);
        setPreviewData(null);
        setItems([{ description: "", hsn: "", unit: "", quantity: "", price: "" }]);
        setError(false);
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        axios.get(
            `${process.env.REACT_APP_MY_API}/api/invoices/getInvoiceCount`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((res) => {
                setInvoiceNumber(res.data.nextInvoiceNo)
            }).catch((err) => {
                console.log(err)
            })
    }, [showPreview])

    return (
        <div className="flex justify-center min-h-screen bg-gray-100 p-4">
            <Paper className="w-full max-w-4xl p-6">
                <Typography variant="h5" className="mb-4">New Invoice</Typography>
                <Grid container spacing={2}>
                    {/* Client Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Client Name"
                            fullWidth
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                        />
                    </Grid>

                    {/* Client Address */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Client Address"
                            fullWidth
                            value={clientAddress}
                            onChange={(e) => setClientAddress(e.target.value)}
                        />
                    </Grid>

                    {/* Client Mobile */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Client Mobile"
                            fullWidth
                            value={clientMobile}
                            onChange={(e) => setClientMobile(e.target.value)}
                            inputProps={{ maxLength: 10 }}
                            helperText={clientMobile.length > 0 && clientMobile.length !== 10 ? 'Enter valid 10-digit mobile number' : ' '}
                            error={clientMobile.length > 0 && clientMobile.length !== 10}
                        />
                    </Grid>

                    {/* GSTIN */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="GSTIN"
                            fullWidth
                            value={gstin}
                            onChange={handleChange}
                            error={gstin.length > 0 && error}
                            helperText={gstin.length > 0 && error ? 'Invalid GSTIN format' : ' '}
                        />
                    </Grid>

                    {/* Invoice Date */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            type="date"
                            label="Invoice Date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={invoiceDate}
                            onChange={(e) => setInvoiceDate(e.target.value)}
                        />
                    </Grid>

                    {/* Due Date */}
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            type="date"
                            label="Due Date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                        />
                    </Grid>
                </Grid>


                <Typography variant="h6" className="mt-6 mb-2">Items</Typography>
                {items.map((item, i) => (
                    <Grid container spacing={2} key={i} className="mb-2">
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Description"
                                fullWidth
                                value={item.description}
                                onChange={(e) => updateItem(i, "description", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Hsn/Code"
                                fullWidth
                                value={item.hsn}
                                onChange={(e) => updateItem(i, "hsn", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Unit"
                                fullWidth
                                value={item.unit}
                                onChange={(e) => updateItem(i, "unit", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Qty"
                                type="number"
                                fullWidth
                                value={item.quantity}
                                onChange={(e) => updateItem(i, "quantity", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Price"
                                type="number"
                                fullWidth
                                value={item.price}
                                onChange={(e) => updateItem(i, "price", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={6} sm={2} className="flex items-center justify-center">
                            <IconButton onClick={() => removeItem(i)}>
                                <Delete />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}

                <Button onClick={addItem} startIcon={<Add />} className="mb-4">Add Item</Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePreview}
                >
                    Preview Invoice
                </Button>
                {showPreview && (
                    <InvoicePreview
                        key={JSON.stringify(invoiceData)} // force remount if data changes
                        invoiceData={invoiceData}
                        setShowPreview={setShowPreview}
                        resetForm={resetForm}
                        onClose={() => setShowPreview(false)}
                    />
                )}


            </Paper>
        </div>
    );
};

export default InvoiceForm;
